import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { FiSetupStatus } from '@core/models';

interface SetupStep {
  label: string;
  completed: boolean;
}

@Component({
  selector: 'app-setup-checklist-card',
  imports: [
    RouterLink,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatProgressBarModule,
  ],
  templateUrl: './setup-checklist-card.component.html',
  styleUrl: './setup-checklist-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SetupChecklistCardComponent {
  status = input<FiSetupStatus | null>(null);
  title = input('Complete Your FI Setup');

  steps = computed<SetupStep[]>(() => {
    const status = this.status();
    if (!status) return [];

    return [
      {
        label: 'FI goal configured',
        completed: status.fiGoalConfigured,
      },
      {
        label: 'At least one asset account',
        completed: status.hasAssetAccount,
      },
      {
        label: 'At least one asset snapshot',
        completed: status.hasAssetSnapshot,
      },
      {
        label: 'At least one monthly snapshot',
        completed: status.hasMonthlySnapshot,
      },
    ];
  });

  progressPercent = computed(() => {
    const status = this.status();
    if (!status || status.totalSteps <= 0) return 0;
    return Math.round((status.completedSteps / status.totalSteps) * 100);
  });

  nextAction = computed(() => {
    const status = this.status();
    if (!status) return null;

    const step = (status.nextRecommendedStep ?? '').toLowerCase();
    switch (step) {
      case 'set-fi-goal':
      case 'configure-fi-goal':
      case 'calculate-fi-goal':
      case 'add-fi-goal':
        return { label: 'Set FI Goal', route: '/fi-goal/setup' };
      case 'add-asset-account':
        return { label: 'Add Asset Account', route: '/assets/new' };
      case 'add-asset-snapshot':
        return { label: 'Add Asset Snapshot', route: '/assets' };
      case 'add-monthly-snapshot':
        return { label: 'Add Monthly Snapshot', route: '/snapshots/new' };
      default:
        return null;
    }
  });
}
